import DataGridSkeleton, { PLACEHOLDER_COLUMNS } from '@/components/DataGrid/DataGridSkeleton';

interface ListPageSkeletonProps {
  /** Columns the real grid draws, so the placeholder is its shape. */
  columns?: number;
  /** Rows to stand in for, matching the list's default page size. */
  rows?: number;
}

/**
 * Loading placeholder for a list page, shaped like the page itself: the CTA on
 * its own row, then the card — gray header strip carrying the title and the
 * search/filter chrome, the table below it, the pagination bar last.
 *
 * It stands in while the route's chunk is still loading, before the page
 * component exists to render `DataGrid`'s own skeleton. The route used to show
 * a centred spinner over the whole viewport there, which told the reader
 * nothing about what was coming and made the page snap into place on arrival.
 */
export default function ListPageSkeleton({ columns = 6, rows = 10 }: ListPageSkeletonProps) {
  return (
    <div className="w-full p-4 md-768:p-6" data-testid="list-page-skeleton">
      <div className="w-full animate-pulse space-y-3 md-768:space-y-6">
        {/* The page CTA — h-10, the `lg` ActionButton it replaces */}
        <div className="flex items-center justify-end">
          <div className="h-10 w-40 rounded-[12px] bg-ds-dark-6" />
        </div>

        <div className="rounded-[10px] border border-ds-border bg-white">
          {/* Header strip: title and count on the left, chrome on the right */}
          <div className="flex flex-col items-stretch gap-3 rounded-t-[10px] bg-ds-dark-2 px-4 pt-3 pb-[22px] md-768:flex-row md-768:items-center md-768:justify-between md-768:px-5">
            <div className="flex items-center gap-2">
              <div className="h-5 w-24 rounded bg-ds-dark-6" />
              <div className="h-6 w-8 rounded-full bg-ds-dark-6" />
            </div>
            {/* Search, provider filter and the status control. White with a
                border, because that is what they are — a flat gray block here
                would not be the shape that lands. */}
            <div className="flex items-center gap-2">
              <div className="h-9 w-full rounded-[12px] border border-ds-border bg-white md-768:w-[220px]" />
              <div className="hidden h-9 w-[200px] rounded-[12px] border border-ds-border bg-white md-768:block" />
              <div className="hidden h-9 w-9 rounded-[12px] border border-ds-border bg-white md-768:block" />
            </div>
          </div>

          {/* The seam: the body pulls up 10px over the strip, as the real card does */}
          <div className="relative -mt-[10px] overflow-hidden rounded-t-[10px] rounded-b-[10px] border-t border-ds-border bg-white">
            <DataGridSkeleton columns={PLACEHOLDER_COLUMNS(columns)} rowCount={rows} />
            {/* Pagination bar, flush inside the card like the real one */}
            <div className="flex items-center justify-between border-t border-ds-border px-4 py-3">
              <div className="h-8 w-36 rounded-[12px] bg-ds-dark-6" />
              <div className="flex items-center gap-1">
                <div className="h-7 w-7 rounded-[10px] bg-ds-dark-6" />
                <div className="h-7 w-7 rounded-[10px] bg-ds-dark-6" />
                <div className="h-7 w-7 rounded-[10px] bg-ds-dark-6" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
